import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const HistoryTab = ({ contractId }) => {
  const [expandedEntries, setExpandedEntries] = useState([]);
  const [filterType, setFilterType] = useState('all');

  const historyEntries = [
    {
      id: 1,
      type: 'created',
      title: 'Contract Created',
      description: 'Initial contract document created and uploaded to the system',
      user: 'John Mitchell',
      userRole: 'Contract Manager',
      timestamp: '2024-01-15T09:00:00Z',
      details: `Contract CT-2024-001 was created with the following initial parameters:\n• Contract Value: $250,000\n• Duration: 12 months\n• Parties: TechCorp Solutions & Innovate Services LLC\n• Initial status set to Draft`,
      metadata: {
        version: '1.0',
        fileSize: '2.4 MB',
        documentType: 'Master Service Agreement'
      }
    },
    {
      id: 2,
      type: 'modified',
      title: 'Contract Terms Updated',
      description: 'Payment terms and deliverables section modified',
      user: 'Sarah Johnson',
      userRole: 'Legal Counsel',
      timestamp: '2024-01-18T14:30:00Z',
      details: `The following sections were modified:\n• Payment Terms: Updated from Net 30 to Net 45 days\n• Deliverables: Added quarterly reporting requirements\n• Milestone 3 deadline extended by 2 weeks`,
      metadata: {
        version: '1.1',
        changedFields: ['paymentTerms', 'deliverables', 'milestones'],
        approvalRequired: true
      }
    },
    {
      id: 3,
      type: 'approved',
      title: 'Changes Approved',
      description: 'Contract modifications approved by legal team',
      user: 'Michael Rodriguez',
      userRole: 'Senior Legal Counsel',
      timestamp: '2024-01-20T11:15:00Z',
      details: `All pending changes have been reviewed and approved:\n• Payment terms modification approved\n• Deliverables updates confirmed\n• Contract ready for signature phase`,
      metadata: {
        approvalId: 'APP-2024-0015',
        approvalMethod: 'Digital Review',
        complianceCheck: 'Passed'
      }
    },
    {
      id: 4,
      type: 'signed',
      title: 'Contract Signed',
      description: 'Contract signed by TechCorp Solutions representative',
      user: 'John Mitchell',
      userRole: 'CTO, TechCorp Solutions',
      timestamp: '2024-01-22T16:45:00Z',
      details: `Digital signature completed:\n• Signatory: John Mitchell (CTO)\n• IP Address: 192.168.1.100\n• Authentication: Two-factor verified\n• Certificate: Valid SSL signature`,
      metadata: {
        signatureId: 'SIG-2024-0001',
        ipAddress: '192.168.1.100',
        authMethod: '2FA',
        certificateValid: true
      }
    },
    {
      id: 5,
      type: 'milestone',
      title: 'Milestone Completed',
      description: 'Project kickoff milestone marked as complete',
      user: 'System',
      userRole: 'Automated',
      timestamp: '2024-02-01T10:00:00Z',
      details: `Milestone "Project Kickoff" has been completed:\n• All required documentation submitted\n• Stakeholder meeting conducted\n• Project timeline confirmed\n• Next milestone: Requirements Analysis (Due: Feb 15)`,
      metadata: {
        milestoneId: 'MS-001',
        completionRate: '100%',
        nextMilestone: 'MS-002',
        daysAhead: 2
      }
    },
    {
      id: 6,
      type: 'amendment',
      title: 'Amendment Added',
      description: 'Amendment 01 - Scope Change added to contract',
      user: 'Sarah Johnson',
      userRole: 'Contract Manager',
      timestamp: '2024-02-20T13:20:00Z',
      details: `Amendment 01 has been added to the contract:\n• Scope expansion for additional services\n• Budget increase: $50,000\n• Timeline extension: 2 months\n• Requires counter-party signature`,
      metadata: {
        amendmentId: 'AMD-001',
        valueChange: '+$50,000',
        timelineChange: '+60 days',
        status: 'Pending Signature'
      }
    }
  ];

  const toggleExpanded = (entryId) => {
    setExpandedEntries(prev => 
      prev?.includes(entryId) 
        ? prev?.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'created': return 'Plus';
      case 'modified': return 'Edit3';
      case 'approved': return 'CheckCircle';
      case 'signed': return 'PenTool';
      case 'milestone': return 'Flag';
      case 'amendment': return 'FileText';
      default: return 'Circle';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'created': return 'text-accent';
      case 'modified': return 'text-warning';
      case 'approved': return 'text-success';
      case 'signed': return 'text-primary';
      case 'milestone': return 'text-success';
      case 'amendment': return 'text-warning';
      default: return 'text-muted-foreground';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredEntries = filterType === 'all' 
    ? historyEntries 
    : historyEntries?.filter(entry => entry?.type === filterType);

  const filterOptions = [
    { value: 'all', label: 'All Events', count: historyEntries?.length },
    { value: 'created', label: 'Created', count: historyEntries?.filter(e => e?.type === 'created')?.length },
    { value: 'modified', label: 'Modified', count: historyEntries?.filter(e => e?.type === 'modified')?.length },
    { value: 'approved', label: 'Approved', count: historyEntries?.filter(e => e?.type === 'approved')?.length },
    { value: 'signed', label: 'Signed', count: historyEntries?.filter(e => e?.type === 'signed')?.length },
    { value: 'milestone', label: 'Milestones', count: historyEntries?.filter(e => e?.type === 'milestone')?.length },
    { value: 'amendment', label: 'Amendments', count: historyEntries?.filter(e => e?.type === 'amendment')?.length }
  ];

  return (
    <div className="p-6 bg-background">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Contract History</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Complete audit trail of contract changes and events
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
          >
            Export History
          </Button>
        </div>

        {/* Filter Tabs */}
        <div className="bg-card rounded-lg border border-border mb-6">
          <div className="p-4 border-b border-border">
            <div className="flex flex-wrap gap-2">
              {filterOptions?.map((option) => (
                <button
                  key={option?.value}
                  onClick={() => setFilterType(option?.value)}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-legal-fast ${
                    filterType === option?.value
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  {option?.label}
                  <span className="ml-2 px-2 py-0.5 bg-muted text-muted-foreground rounded-full text-xs">
                    {option?.count}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-card rounded-lg border border-border">
          <div className="p-6 border-b border-border">
            <h3 className="text-lg font-medium text-foreground">Activity Timeline</h3>
          </div>
          
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border"></div>
            
            <div className="space-y-0">
              {filteredEntries?.map((entry, index) => (
                <div key={entry?.id} className="relative">
                  <div className="flex items-start space-x-6 p-6 hover:bg-muted/30 transition-legal-fast">
                    {/* Timeline Dot */}
                    <div className={`relative z-10 w-8 h-8 rounded-full bg-card border-2 border-border flex items-center justify-center ${getTypeColor(entry?.type)}`}>
                      <Icon name={getTypeIcon(entry?.type)} size={16} />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-medium text-foreground">{entry?.title}</h4>
                          <p className="text-sm text-muted-foreground mt-1">{entry?.description}</p>
                        </div>
                        <div className="text-right text-sm text-muted-foreground ml-4">
                          <p>{formatDate(entry?.timestamp)}</p>
                          <p className="mt-1">{entry?.user}</p>
                          <p className="text-xs">{entry?.userRole}</p>
                        </div>
                      </div>
                      
                      {/* Expandable Details */}
                      {entry?.details && (
                        <div className="mt-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName={expandedEntries?.includes(entry?.id) ? 'ChevronUp' : 'ChevronDown'}
                            iconPosition="left"
                            onClick={() => toggleExpanded(entry?.id)}
                          >
                            {expandedEntries?.includes(entry?.id) ? 'Hide' : 'Show'} Details
                          </Button>
                          
                          {expandedEntries?.includes(entry?.id) && (
                            <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                              <div className="whitespace-pre-line text-sm text-foreground mb-4">
                                {entry?.details}
                              </div>
                              
                              {entry?.metadata && (
                                <div className="border-t border-border pt-4">
                                  <h5 className="text-sm font-medium text-foreground mb-2">Metadata</h5>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                    {Object.entries(entry?.metadata)?.map(([key, value]) => (
                                      <div key={key} className="flex justify-between">
                                        <span className="text-muted-foreground capitalize">
                                          {key?.replace(/([A-Z])/g, ' $1')?.trim()}:
                                        </span>
                                        <span className="text-foreground font-medium">
                                          {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {index < filteredEntries?.length - 1 && (
                    <div className="border-b border-border ml-14"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoryTab;